import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  Chip,
  useTheme,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import { Delete, Save, ArrowBack, CloudUpload } from '@mui/icons-material';
import knowledgeApi from '../api/knowledgeApi';

const EditKnowledgeBase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // State declarations
  const [kbData, setKbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Fetch knowledge base data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await knowledgeApi.get(id);
        setKbData(data);
        
        let filesArray = [];
        if (data.Files) {
          if (typeof data.Files === 'string') {
            try {
              filesArray = JSON.parse(data.Files);
            } catch (e) {
              console.error('Error parsing Files JSON:', e);
              filesArray = [];
            }
          } else if (Array.isArray(data.Files)) {
            filesArray = data.Files;
          }
        }
        
        setFiles(filesArray);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching knowledge base:', error);
        setError('Failed to load knowledge base. Please try again.');
        setLoading(false);
      }
    };
    
    if (id) fetchData();
  }, [id]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFileUpload = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles([...files, ...newFiles]);
  };

  const handleFileDelete = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      
      if (!kbData.Name || kbData.Name.trim() === '') {
        setError('Knowledge base name cannot be empty');
        setSaving(false);
        return;
      }
      
      if (files.length === 0) {
        setError('At least one file is required');
        setSaving(false);
        return;
      }
      
      await knowledgeApi.update(id, {
        ...kbData,
        Files: files
      });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      setSaving(false);
    } catch (error) {
      console.error('Error updating knowledge base:', error);
      setError('Failed to update knowledge base. Please try again.');
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await knowledgeApi.delete(id);
      navigate('/dashboard/knowledge');
    } catch (error) {
      console.error('Error deleting knowledge base:', error);
      setError('Failed to delete knowledge base. Please try again.');
    } finally {
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!kbData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || 'Failed to load knowledge base data. Please try again.'}
        </Alert>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/dashboard/knowledge')}
          sx={{ mt: 2 }}
        >
          Back to Knowledge Bases
        </Button>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      height="calc(100vh - 100px)"
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: theme.palette.background.default,
        overflow: 'auto',
        p: 3,
      }}
    >
      {/* Header Section */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ 
          mb: 4,
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2
          }
        }}
      >
        <Typography variant="h5" fontWeight={600}>Edit Knowledge Base</Typography>
        <Stack direction="row" spacing={1.5} sx={{ 
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            justifyContent: 'space-between'
          }
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard/knowledge')}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmDelete(true)}
            sx={{ borderRadius: 1, textTransform: 'none' }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={saving}
            sx={{ 
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 140
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {/* Status Messages */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }}>
            Knowledge base updated successfully!
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Details Section */}
      <Paper sx={{ 
        mb: 4, 
        p: 3, 
        borderRadius: 2,
        boxShadow: theme.shadows[1]
      }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
          Knowledge Base Details
        </Typography>
        
        <TextField
          fullWidth
          label="Knowledge Base Name"
          variant="outlined"
          value={kbData.Name || ''}
          onChange={(e) => setKbData({...kbData, Name: e.target.value})}
          sx={{ mb: 4 }}
        />
        
        <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Chip
              label={kbData.Status || 'Processing'}
              sx={{ 
                mt: 0.5,
                bgcolor: kbData.Status === 'Ready' 
                  ? theme.palette.success.light 
                  : theme.palette.warning.light,
                color: kbData.Status === 'Ready' 
                  ? theme.palette.success.dark 
                  : theme.palette.warning.dark
              }}
              size="small"
            />
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Type</Typography>
            <Typography variant="body2">{kbData.Type || 'Unknown'}</Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Size</Typography>
            <Typography variant="body2">{formatFileSize(kbData.Size || 0)}</Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Last Updated</Typography>
            <Typography variant="body2">
              {kbData.LastUpdated ? new Date(kbData.LastUpdated).toLocaleString() : 'Never'}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Files Section */}
      <Paper sx={{ 
        mb: 4, 
        p: 3, 
        borderRadius: 2,
        boxShadow: theme.shadows[1]
      }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
          File Management
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <input
            accept=".pdf,.txt,.csv,.doc,.docx,.json"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            multiple
            onChange={handleFileUpload}
            disabled={saving}
          />
          <label htmlFor="file-upload">
            <Button 
              variant="outlined" 
              component="span"
              startIcon={<CloudUpload />}
              disabled={saving}
              sx={{
                borderRadius: 1,
                borderStyle: 'dashed',
                textTransform: 'none',
                py: 1.5,
                width: '100%'
              }}
            >
              Upload New Files
            </Button>
          </label>
        </Box>
        
        {files.length > 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1, 
            p: 2, 
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            backgroundColor: theme.palette.background.default,
          }}>
            {files.map((file, index) => (
              <Chip
                key={index}
                label={file.name || file.title || `File ${index + 1}`}
                sx={{ 
                  m: 0.5,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.text.secondary
                  }
                }}
                onDelete={() => handleFileDelete(index)}
                deleteIcon={<Delete />}
                disabled={saving}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ 
            p: 3, 
            textAlign: 'center', 
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: 1,
            backgroundColor: theme.palette.background.default,
          }}>
            <Typography variant="body2" color="textSecondary">
              No files uploaded. Please add at least one file.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Delete Dialog */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle variant="h6">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete "{kbData.Name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDelete(false)}
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditKnowledgeBase;